"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChartBuilder = void 0;
const d3_1 = require("d3");
const ChartSettingsEntity_1 = __importDefault(require("./entities/ChartSettingsEntity"));
const DatasetEntityArrayManager_1 = __importDefault(require("./managers/DatasetEntityArrayManager"));
const ServiceEntityArrayManager_1 = __importDefault(require("./managers/ServiceEntityArrayManager"));
const Chart_1 = require("./Chart");
const BoundarySetArrayManager_1 = __importDefault(require("./managers/BoundarySetArrayManager"));
const EventPointSetManager_1 = __importDefault(require("./managers/EventPointSetManager"));
const ColorGenerator_1 = __importDefault(require("./ColorGenerator"));
const LabelPosition_1 = require("./enums/LabelPosition");
const LegendOrientation_1 = require("./enums/LegendOrientation");
const DataVisualStyle_1 = require("./enums/DataVisualStyle");
const AxisPosition_1 = require("./enums/AxisPosition");
const Services_1 = require("./enums/Services");
const ServiceNames_1 = require("./enums/ServiceNames");
const GRID_TICKS = 5;
const X_TICKS_COUNT = 6;
const Y_TICKS_COUNT = 6;
class ChartBuilder {
    constructor(element, settings, services, data) {
        this._dateFormatter = null;
        this._onlyTimeFormatter = null;
        this._onlyDateFormatter = null;
        this._chartDateFormatter = null;
        this._xAxisTickSize = X_TICKS_COUNT;
        this._yAxisTickSize = Y_TICKS_COUNT;
        this._gridTickSize = GRID_TICKS;
        this._labelPosition = LabelPosition_1.LabelPosition.SIDE;
        this._legendOrientation = LegendOrientation_1.LegendOrientation.ROW;
        this._dateRange = { startTS: null, stopTS: null };
        this._axisScales = [];
        this._valueRangeArray = [];
        this._chartConfiguration = {
            hasBoundaries: false,
            hasEventPoints: false,
            hasXGrid: false,
            hasYGrid: false,
            hasPreviewChart: false,
            isInteractive: false,
            hasLegend: false,
            hasZoomButtons: false,
            hasServiceButtons: false,
            hasScaleButton: false,
            isScaleByData: false,
            hasLoggersFilter: false,
            hasLegendsFilter: false
        };
        this._colorGenerator = new ColorGenerator_1.default();
        this.addElement(element);
        this.addSettings(settings);
        this.addServices(services);
        this.addData(data);
    }
    get chartId() {
        return this._chartId;
    }
    get colorGenerator() {
        return this._colorGenerator;
    }
    get chartConfiguration() {
        return this._chartConfiguration;
    }
    get gridTickSize() {
        return this._gridTickSize;
    }
    get xAxisTickSize() {
        return this._xAxisTickSize;
    }
    get yAxisTickSize() {
        return this._yAxisTickSize;
    }
    get labelPosition() {
        return this._labelPosition;
    }
    get legendOrientation() {
        return this._legendOrientation;
    }
    get element() {
        return this._element;
    }
    get settings() {
        return this._settings;
    }
    get services() {
        return this._services;
    }
    get dataManager() {
        return this._dataManager;
    }
    get boundaryManager() {
        return this._boundaryManager;
    }
    get eventDatasetManager() {
        return this._eventDatasetManager;
    }
    get dateFormatter() {
        return this._dateFormatter;
    }
    get onlyTimeFormatter() {
        return this._onlyTimeFormatter;
    }
    get onlyDateFormatter() {
        return this._onlyDateFormatter;
    }
    get chartDateFormatter() {
        return this._chartDateFormatter;
    }
    get dateRange() {
        return this._dateRange;
    }
    get fontSize() {
        return this._fontSize;
    }
    get initService() {
        return this._initService;
    }
    get axisThickness() {
        return this._axisThickness;
    }
    get axisScaleRange() {
        return this._axisScales;
    }
    get valueRangeArray() {
        return this._valueRangeArray;
    }
    addBoundaries(boundaries, colorFilled = false, pointsColor) {
        this._boundaryManager = new BoundarySetArrayManager_1.default(boundaries, this._colorGenerator, colorFilled, pointsColor);
        this._chartConfiguration.hasBoundaries = boundaries.length > 0 ? true : false;
        return this;
    }
    makeInteractive() {
        this._chartConfiguration.isInteractive = true;
        return this;
    }
    addZoomButtons() {
        this._chartConfiguration.hasZoomButtons = true;
        return this;
    }
    addServiceButtonsHandling() {
        this._chartConfiguration.hasServiceButtons = true;
        return this;
    }
    addScaleButton(isScaleByData = true) {
        this._chartConfiguration.isScaleByData = isScaleByData;
        this._chartConfiguration.hasScaleButton = true;
        return this;
    }
    addLoggersFilter() {
        this._chartConfiguration.hasLoggersFilter = true;
        return this;
    }
    addLegendsFilter() {
        this._chartConfiguration.hasLegendsFilter = true;
        return this;
    }
    addPreviewChart() {
        this._chartConfiguration.hasPreviewChart = true;
        return this;
    }
    addGridlines(hasXgrid = true, hasYgrid = true, tickSize = GRID_TICKS) {
        this._chartConfiguration.hasXGrid = hasXgrid;
        this._chartConfiguration.hasYGrid = hasYgrid;
        this._gridTickSize = tickSize;
        return this;
    }
    addEventPoints(eventDataset) {
        this._eventDatasetManager = new EventPointSetManager_1.default(eventDataset, this._colorGenerator);
        this._chartConfiguration.hasEventPoints = true;
        return this;
    }
    addLegend(orientation) {
        this._chartConfiguration.hasLegend = true;
        this._legendOrientation = orientation;
        return this;
    }
    setDateFormatter(formatter) {
        this._dateFormatter = formatter;
        return this;
    }
    setOnlyTimeFormatter(formatter) {
        this._onlyTimeFormatter = formatter;
        return this;
    }
    setOnlyDateFormatter(formatter) {
        this._onlyDateFormatter = formatter;
        return this;
    }
    setChartDateFormatter(formatter) {
        this._chartDateFormatter = formatter;
        return this;
    }
    setLabelPosition(position) {
        this._labelPosition = position;
        return this;
    }
    setValueRange(range) {
        this._valueRangeArray.push(range);
        return this;
    }
    setXAxisTickSize(tickSize = X_TICKS_COUNT) {
        this._xAxisTickSize = tickSize;
        return this;
    }
    setYAxisTickSize(tickSize = Y_TICKS_COUNT) {
        this._yAxisTickSize = tickSize;
        return this;
    }
    setDateRange(dateRange) {
        this._dateRange = dateRange;
        return this;
    }
    setFontSize(fontSize) {
        this._fontSize = fontSize;
        return this;
    }
    setInitService(service) {
        this._initService = service;
        return this;
    }
    setAxisThickness(axisThickness) {
        this._axisThickness = axisThickness;
        return this;
    }
    setAxisScaleRange(axisScaleRange) {
        this._axisScales = axisScaleRange;
        return this;
    }
    build() {
        return new Chart_1.Chart(this);
    }
    addElement(element) {
        if (!element)
            throw new Error("Chart element can\'t be null|undefined");
        if (typeof element == 'string') {
            this._chartId = element;
            this._element = (0, d3_1.select)('#' + element);
        }
        else if (typeof element == 'object') {
            this._chartId = element.attr('id');
            this._element = element;
        }
    }
    addSettings(settings) {
        this._settings = new ChartSettingsEntity_1.default(settings);
    }
    addServices(services) {
        if (!services || services.length < 1)
            throw new Error("Service array is required");
        this.setAxisPosition(services);
        this._services = new ServiceEntityArrayManager_1.default(services);
    }
    setAxisPosition(services) {
        services = services.filter(service => service.id !== Services_1.Services.ANNOTATION);
        const pressureServices = services.filter(service => service.id == Services_1.Services.PRESSURE);
        if (pressureServices.length > 1) {
            services = services.filter(service => service.originalName !== ServiceNames_1.ServiceNames.ATMOSPHERICPRESSURE);
        }
        const alreadyUsedPositions = services.map(x => x.axisPosition).filter(y => y != undefined);
        const availablePositions = [AxisPosition_1.AxisPosition.LEFT_OUTSIDE, AxisPosition_1.AxisPosition.RIGHT_OUTSIDE, AxisPosition_1.AxisPosition.LEFT_INSIDE, AxisPosition_1.AxisPosition.RIGHT_INSIDE].filter(x => !alreadyUsedPositions.includes(x));
        if (services.length - alreadyUsedPositions.length > availablePositions.length)
            throw new Error("Max 4 services can be visualized.");
        services.forEach(service => {
            if (!service.axisPosition && service.axisPosition != 0) {
                service.axisPosition = availablePositions.shift();
            }
        });
    }
    addData(data) {
        if (!data || data.length < 1)
            throw new Error("Data array is required");
        this.hasServicesForData(data);
        this.addStyleToData(data);
        let needToAddRed = true;
        data.forEach(dataset => {
            if (dataset.serviceId == 5) {
                const red = '#ff0000';
                dataset.color = red;
                if (needToAddRed) {
                    this.colorGenerator.addToPickedColors(red);
                    needToAddRed = false;
                }
            }
        });
        this._dataManager = new DatasetEntityArrayManager_1.default(data, this._colorGenerator);
    }
    addStyleToData(data) {
        data.forEach(dataset => {
            if (!dataset.style) {
                dataset.style = this.getVisualStyleByUnitId(dataset.serviceId);
            }
            const filteredData = dataset.data.filter(x => x.value != null);
            if (filteredData.length == 1 && dataset.style == DataVisualStyle_1.DataVisualStyle.LINE) {
                dataset.data = filteredData;
                dataset.style = DataVisualStyle_1.DataVisualStyle.POINTS;
            }
        });
    }
    getVisualStyleByUnitId(unitId) {
        switch (unitId) {
            case Services_1.Services.TEMPERATURE:
                return DataVisualStyle_1.DataVisualStyle.LINE;
                break;
            case Services_1.Services.HUMIDITY:
                return DataVisualStyle_1.DataVisualStyle.LINE;
                break;
            case Services_1.Services.UPTIME:
                return DataVisualStyle_1.DataVisualStyle.UPTIMERECTANGLE;
                break;
            case Services_1.Services.ANNOTATION:
                return DataVisualStyle_1.DataVisualStyle.ANNOTATIONRECTANGLE;
                break;
            default:
                return DataVisualStyle_1.DataVisualStyle.LINE;
                break;
        }
    }
    hasServicesForData(data) {
        const services = this._services.services;
        data.forEach(d => {
            if (services.find(x => x.id == d.serviceId) == null)
                throw new Error("Missing service for given data (serviceId: " + d.serviceId + ")");
        });
    }
}
exports.ChartBuilder = ChartBuilder;
